import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { crmService } from '../../../services/crmService';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const QuickActions = ({ onShareReferral, onImportLead, onRedeemPoints }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [showImportLeadModal, setShowImportLeadModal] = useState(false);
  const [showRedeemPointsModal, setShowRedeemPointsModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Lead form state
  const [leadData, setLeadData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    proyecto_interes: '',
    presupuesto: ''
  });

  const referralLink = `${window.location?.origin}/landing-page?ref=${user?.id || 'USER123'}`;

  const handleCopyLink = () => {
    navigator.clipboard?.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShareEmail = () => {
    const subject = encodeURIComponent('Únete a Residencial 3 Volcanes');
    const body = encodeURIComponent(`Hola,\n\nTe invito a conocer Residencial 3 Volcanes, un proyecto inmobiliario excepcional.\n\nUsa mi link de referido: ${referralLink}\n\n¡Espero verte pronto!`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleImportLead = async (e) => {
    e?.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Validate form
      if (!leadData?.nombre || !leadData?.email || !leadData?.telefono) {
        setError('Por favor, completa todos los campos requeridos');
        setLoading(false);
        return;
      }

      // Create lead
      await crmService?.createLeadWithAI(leadData, user?.id);
      
      setSuccess('¡Lead importado exitosamente!');
      setLeadData({
        nombre: '',
        email: '',
        telefono: '',
        proyecto_interes: '',
        presupuesto: ''
      });
      
      setTimeout(() => {
        setShowImportLeadModal(false);
        setSuccess('');
        if (onImportLead) onImportLead();
      }, 2000);
    } catch (err) {
      console.error('Import lead error:', err);
      setError('Error al importar lead. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleRedeemPoints = () => {
    setShowRedeemPointsModal(true);
  };

  return (
    <>
      <div className="card">
        <h3 className="text-xl md:text-2xl font-semibold text-foreground font-heading mb-6">
          Acciones Rápidas
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Button
            variant="default"
            fullWidth
            iconName="Share2"
            iconPosition="left"
            onClick={() => setShowReferralModal(true)}
          >
            Compartir Referido
          </Button>

          <Button
            variant="outline"
            fullWidth
            iconName="UserPlus"
            iconPosition="left"
            onClick={() => setShowImportLeadModal(true)}
          >
            Importar Lead
          </Button>

          <Button
            variant="secondary"
            fullWidth
            iconName="Gift"
            iconPosition="left"
            onClick={handleRedeemPoints}
          >
            Canjear Puntos
          </Button>

          <Button
            variant="outline"
            fullWidth
            iconName="Megaphone"
            iconPosition="left"
            onClick={() => navigate('/social-media-marketing-hub')}
          >
            Marketing Hub
          </Button>
        </div>
      </div>
      {/* Referral Modal */}
      {showReferralModal && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-background/80 backdrop-blur-sm"
            onClick={() => setShowReferralModal(false)}
          />
          
          <div className="relative bg-card rounded-xl shadow-lg max-w-md w-full p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-semibold text-foreground font-heading">
                Compartir Link de Referido
              </h3>
              <button
                onClick={() => setShowReferralModal(false)}
                className="p-2 rounded-xl hover:bg-muted transition-smooth focus-ring"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Tu Link de Referido
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={referralLink}
                    readOnly
                    className="flex-1 px-4 py-3 rounded-xl border border-input bg-muted text-sm data-text"
                  />
                  <Button
                    variant={copied ? "success" : "outline"}
                    iconName={copied ? "Check" : "Copy"}
                    onClick={handleCopyLink}
                  >
                    {copied ? 'Copiado' : 'Copiar'}
                  </Button>
                </div>
              </div>

              <div className="border-t border-border pt-6">
                <p className="text-sm text-muted-foreground mb-4">
                  Compartir vía:
                </p>
                <div className="grid grid-cols-1 gap-3">
                  <Button
                    variant="outline"
                    fullWidth
                    iconName="Mail"
                    iconPosition="left"
                    onClick={handleShareEmail}
                  >
                    Email
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Import Lead Modal */}
      {showImportLeadModal && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-background/80 backdrop-blur-sm"
            onClick={() => !loading && setShowImportLeadModal(false)}
          />
          
          <div className="relative bg-card rounded-xl shadow-lg max-w-md w-full p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-semibold text-foreground font-heading">
                Importar Lead
              </h3>
              <button
                onClick={() => !loading && setShowImportLeadModal(false)}
                className="p-2 rounded-xl hover:bg-muted transition-smooth focus-ring"
                disabled={loading}
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            <form onSubmit={handleImportLead} className="space-y-4">
              {error && (
                <div className="bg-error/10 border border-error/20 rounded-lg p-3 flex items-start space-x-2">
                  <Icon name="AlertCircle" size={18} color="var(--color-error)" />
                  <p className="text-sm text-error flex-1">{error}</p>
                </div>
              )}

              {success && (
                <div className="bg-success/10 border border-success/20 rounded-lg p-3 flex items-start space-x-2">
                  <Icon name="CheckCircle" size={18} color="var(--color-success)" />
                  <p className="text-sm text-success flex-1">{success}</p>
                </div>
              )}

              <Input
                type="text"
                label="Nombre Completo"
                placeholder="Juan Pérez"
                value={leadData?.nombre}
                onChange={(e) => setLeadData({ ...leadData, nombre: e?.target?.value })}
                required
                disabled={loading}
              />

              <Input
                type="email"
                label="Email"
                placeholder="juan@email.com"
                value={leadData?.email}
                onChange={(e) => setLeadData({ ...leadData, email: e?.target?.value })}
                required
                disabled={loading}
              />

              <Input
                type="tel"
                label="Teléfono"
                placeholder="987654321"
                value={leadData?.telefono}
                onChange={(e) => setLeadData({ ...leadData, telefono: e?.target?.value })}
                required
                disabled={loading}
              />

              <Input
                type="text"
                label="Proyecto de Interés"
                placeholder="Residencial 3 Volcanes"
                value={leadData?.proyecto_interes}
                onChange={(e) => setLeadData({ ...leadData, proyecto_interes: e?.target?.value })}
                disabled={loading}
              />

              <Input
                type="number"
                label="Presupuesto (S/)"
                placeholder="180000"
                value={leadData?.presupuesto}
                onChange={(e) => setLeadData({ ...leadData, presupuesto: e?.target?.value })}
                disabled={loading}
              />

              <div className="flex space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  fullWidth
                  onClick={() => setShowImportLeadModal(false)}
                  disabled={loading}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  fullWidth
                  loading={loading}
                  disabled={loading}
                >
                  Importar Lead
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Redeem Points Modal */}
      {showRedeemPointsModal && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-background/80 backdrop-blur-sm"
            onClick={() => setShowRedeemPointsModal(false)}
          />
          
          <div className="relative bg-card rounded-xl shadow-lg max-w-md w-full p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-semibold text-foreground font-heading">
                Canjear Puntos
              </h3>
              <button
                onClick={() => setShowRedeemPointsModal(false)}
                className="p-2 rounded-xl hover:bg-muted transition-smooth focus-ring"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
                <Icon name="Gift" size={32} color="var(--color-primary)" />
              </div>
              <h4 className="text-xl font-semibold text-foreground mb-2">
                Sistema de Recompensas
              </h4>
              <p className="text-muted-foreground mb-6">
                El sistema de canje de puntos estará disponible próximamente. Sigue acumulando puntos con tus ventas y referencias.
              </p>
              <Button
                variant="outline"
                onClick={() => setShowRedeemPointsModal(false)}
              >
                Entendido
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default QuickActions;